import { isNil } from 'lodash-es'
import { useTranslations } from 'next-intl'

import { useFormatCurrency, useSession } from '@/shared/lib'
import { Separator, Skeleton, Description } from '@/shared/ui'

import { useContract } from './provider'
import { useContractEstimate } from '../../../api/get-contract-estimate'

type Props = {
  roomId: number
}

export const PriceInformation = ({ roomId }: Props) => {
  const t = useTranslations('common')

  const { formatCurrency } = useFormatCurrency()

  const { period, hasPeriodSelected } = useContract()
  const session = useSession()
  const isLoggedIn = !!session.data

  const { estimate, isLoading } = useContractEstimate({
    roomId,
    period: period,
    isExtend: false,
    enabled: hasPeriodSelected && isLoggedIn,
  })

  if (isLoading) {
    return <PriceInformationSkeleton />
  }

  if (isNil(estimate)) {
    return null
  }

  const showLongTermDiscount = estimate.longTermDiscount > 0
  const showEarlyDiscount = estimate.earlyDiscount > 0

  return (
    <div className="space-y-3 px-2.5">
      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel normal>{t('rent')}</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.usingFee)}
          </Description.ItemValue>
        </Description.Item>

        {showLongTermDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel normal highlight>
              {t('discount.long_term')}
            </Description.ItemLabel>
            <Description.ItemValue highlight>
              {formatCurrency(estimate.longTermDiscount, {
                negative: true,
              })}
            </Description.ItemValue>
          </Description.Item>
        )}
        {showEarlyDiscount && (
          <Description.Item className="justify-between">
            <Description.ItemLabel normal highlight>
              {t('discount.immediate')}
            </Description.ItemLabel>
            <Description.ItemValue highlight>
              {formatCurrency(estimate.earlyDiscount, {
                negative: true,
              })}
            </Description.ItemValue>
          </Description.Item>
        )}

        <Description.Item className="justify-between">
          <Description.ItemLabel normal>
            {t('utility_fee')}
          </Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.mgmtFee)}
          </Description.ItemValue>
        </Description.Item>

        <Description.Item className="items-start justify-between">
          <Description.ItemLabel normal>
            {t('cleaning_fee')}
          </Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.cleanFee)}
          </Description.ItemValue>
        </Description.Item>

        <Description.Item className="justify-between">
          <Description.ItemLabel normal>
            {t('service_fee')}
          </Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.contractFee)}
          </Description.ItemValue>
        </Description.Item>
      </Description.List>

      <Separator />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="text-black">
            {t('total_payment')}
          </Description.ItemLabel>
          <Description.ItemValue bold highlight className="typo-body1">
            {formatCurrency(estimate.realUsingFee)}
          </Description.ItemValue>
        </Description.Item>

        <Description.Item className="justify-between">
          <Description.ItemLabel normal>{t('deposit')}</Description.ItemLabel>
          <Description.ItemValue>
            {formatCurrency(estimate.deposit)}
          </Description.ItemValue>
        </Description.Item>

        <p className="typo-caption1 text-center font-normal text-black">
          {t('deposit_notice')}
        </p>
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Description.ItemLabel semiBold className="text-black">
            {t('total_payment')}
          </Description.ItemLabel>
          <Description.ItemValue bold className="typo-body1 text-black">
            {formatCurrency(estimate.finalPayFee)}
          </Description.ItemValue>
        </Description.Item>
      </Description.List>
    </div>
  )
}

export const PriceInformationSkeleton = () => {
  return (
    <div className="space-y-3 px-2.5">
      <Description.List>
        {Array.from({ length: 6 }).map((_, index) => (
          <Description.Item key={index} className="justify-between">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16" />
          </Description.Item>
        ))}
      </Description.List>

      <Separator />

      <Description.List>
        {Array.from({ length: 2 }).map((_, index) => (
          <Description.Item key={index} className="justify-between">
            <Skeleton className="h-5 w-12" />
            <Skeleton className="h-5 w-16" />
          </Description.Item>
        ))}

        <Skeleton className="h-4 w-full" />
      </Description.List>

      <Separator color="black" />

      <Description.List>
        <Description.Item className="justify-between">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-6 w-18" />
        </Description.Item>
      </Description.List>
    </div>
  )
}
